/**
 * Month calendar view for the orders tracker. ADDITIVE ONLY: it renders the
 * rows the page's own list query returned under the page's own filters and
 * RLS (the page fetches them with the export precedent
 * { ...queryParams, page: 0, pageSize: 5000 }); this component does no
 * filtering of its own beyond placing rows on days, so the status chips,
 * family filter and search drive it exactly as they drive the table.
 *
 * Placement key: expected_delivery_date (when the pair arrives). Dateless
 * and out-of-range dates (the year-0020 class) surface in a labelled
 * bucket rather than vanishing: BUCKETED, not clamped, so no order is ever
 * shown on a fabricated day.
 *
 * Entry = order number, patient, product, colour-coded by the SAME status
 * tokens the table's badges read (colors.status). A day shows everything
 * up to six entries; beyond that the cell offers "+N more" and the full
 * list lives in the day panel, which renders each order through
 * renderRowCard, the page's own OrderRow variant="card", so the panel
 * carries the table's row actions without duplicating a single handler.
 */
import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  Skeleton,
  colors,
} from '@insolech/ui';
import type { StatusKey } from '@insolech/ui';
import type { OrderListRow } from '@insolech/sdk';
import { formatProductLabel, resolvePatientDisplay } from '../productLabel';
import {
  addMonths,
  bucketOrdersByDeliveryDay,
  monthGrid,
  monthLabel,
  monthOf,
  sameMonth,
  type CalendarMonth,
} from './calendarBuckets';

// Same page-local mapping the tracker and its siblings carry.
const STATUS_TO_KEY: Record<string, StatusKey> = {
  draft: 'draft',
  submitted: 'submitted',
  in_review: 'in_review',
  in_production: 'in_production',
  in_delivery: 'in_delivery',
  delivered: 'delivered',
  cancelled: 'cancelled',
  returned: 'returned',
  on_hold: 'on_hold',
};

/** A realistic busy day is five or six arrivals: show them all. Only a
 *  genuinely overflowing cell (7+) collapses behind "+N more". */
const MAX_VISIBLE_PER_DAY = 6;

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface Props {
  rows: readonly OrderListRow[];
  isLoading: boolean;
  isError: boolean;
  errorText?: string | undefined;
  /** Renders one order with the table's own card row (actions included). */
  renderRowCard: (row: OrderListRow) => React.ReactNode;
  /** Test hook: pin the opening month (defaults to the current month). */
  initialMonth?: CalendarMonth | undefined;
  /** Test hook: pin the highlighted current day (defaults to the local today). */
  todayIso?: string | undefined;
}

/** Local-time yyyy-mm-dd (NOT toISOString, which is UTC and would highlight
 *  the wrong cell near midnight in the UAE). */
function localDayIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function entryLabel(row: OrderListRow): { patient: string; product: string } {
  const patient = resolvePatientDisplay(row).name ?? '-';
  const line = row.primary_line ?? row.lines[0] ?? null;
  return { patient, product: formatProductLabel(line) };
}

function statusColor(row: OrderListRow): string {
  const key = (STATUS_TO_KEY[row.status] ?? 'draft') as StatusKey;
  return colors.status[key]?.dot ?? colors.status.draft.dot;
}

function fullDayLabel(iso: string): string {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function OrdersCalendarView({
  rows,
  isLoading,
  isError,
  errorText,
  renderRowCard,
  initialMonth,
  todayIso,
}: Props): React.ReactElement {
  const [month, setMonth] = React.useState<CalendarMonth>(initialMonth ?? monthOf(new Date()));
  /** A yyyy-mm-dd day, or the no-date bucket, or closed. */
  const [panel, setPanel] = React.useState<string | 'no-date' | null>(null);

  const { byDay, noDate } = React.useMemo(() => bucketOrdersByDeliveryDay(rows), [rows]);
  const weeks = React.useMemo(() => monthGrid(month), [month]);
  const today = monthOf(new Date());
  const currentDayIso = todayIso ?? localDayIso(new Date());

  const panelRows: OrderListRow[] =
    panel === null ? [] : panel === 'no-date' ? noDate : (byDay.get(panel) ?? []);

  if (isLoading) {
    return (
      <div className="p-4 flex flex-col gap-2" data-testid="orders-calendar-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <p className="p-4 text-sm text-error-700" data-testid="orders-calendar-error">
        Could not load orders{errorText ? `: ${errorText}` : '.'}
      </p>
    );
  }

  return (
    <div className="px-4 pb-4" data-testid="orders-calendar">
      <div className="flex flex-wrap items-center gap-2 py-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setMonth((m) => addMonths(m, -1))}
          aria-label="Previous month"
          data-testid="calendar-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-[10rem] text-center text-sm font-medium text-text-primary" data-testid="calendar-month-label">
          {monthLabel(month)}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setMonth((m) => addMonths(m, 1))}
          aria-label="Next month"
          data-testid="calendar-next"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMonth(monthOf(new Date()))}
          disabled={sameMonth(month, today)}
          data-testid="calendar-today"
        >
          Today
        </Button>
        {noDate.length > 0 ? (
          <button
            type="button"
            className="ms-auto rounded-full border border-border-default px-3 py-1 text-xs text-text-secondary hover:bg-surface-muted"
            onClick={() => setPanel('no-date')}
            data-testid="calendar-no-date-bucket"
          >
            No or invalid expected date ({noDate.length})
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-7 gap-px rounded-md border border-border-subtle bg-border-subtle overflow-hidden">
        {WEEKDAYS.map((d) => (
          <div key={d} className="bg-surface px-2 py-1 text-center text-xs font-medium text-text-secondary">
            {d}
          </div>
        ))}
        {weeks.flat().map((cell) => {
          const dayRows = byDay.get(cell.iso) ?? [];
          const overflowing = dayRows.length > MAX_VISIBLE_PER_DAY;
          const visible = overflowing ? dayRows.slice(0, MAX_VISIBLE_PER_DAY - 1) : dayRows;
          return (
            <div
              key={cell.iso}
              className={`min-h-[7rem] bg-surface-elevated p-1 ${cell.inMonth ? '' : 'opacity-50'}`}
              data-testid={`calendar-cell-${cell.iso}`}
            >
              <button
                type="button"
                className="mb-1 block w-full text-start text-xs font-medium text-text-secondary hover:text-text-primary"
                onClick={() => (dayRows.length > 0 ? setPanel(cell.iso) : undefined)}
                aria-label={`Open ${cell.iso}`}
              >
                {cell.iso === currentDayIso ? (
                  <span
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 font-semibold text-text-onBrand"
                    data-testid="calendar-today-badge"
                  >
                    {cell.day}
                  </span>
                ) : (
                  cell.day
                )}
              </button>
              <div className="flex flex-col gap-0.5">
                {visible.map((row) => {
                  const { patient, product } = entryLabel(row);
                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => setPanel(cell.iso)}
                      className={`w-full truncate rounded-sm border-s-4 px-1 py-0.5 text-start text-[11px] leading-tight text-text-primary hover:bg-surface-muted ${
                        row.is_urgent ? 'bg-warning-50' : ''
                      }`}
                      style={{ borderInlineStartColor: statusColor(row) }}
                      title={`${row.order_number} · ${patient} · ${product}`}
                      data-testid={`calendar-entry-${row.order_number}`}
                    >
                      {row.order_number} · {patient} · {product}
                    </button>
                  );
                })}
                {overflowing ? (
                  <button
                    type="button"
                    className="w-full rounded-sm px-1 py-0.5 text-start text-[11px] text-brand-700 hover:underline"
                    onClick={() => setPanel(cell.iso)}
                    data-testid={`calendar-more-${cell.iso}`}
                  >
                    +{dayRows.length - (MAX_VISIBLE_PER_DAY - 1)} more
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <Modal open={panel !== null} onOpenChange={(o) => (o ? undefined : setPanel(null))}>
        <ModalContent size="lg">
          <ModalHeader>
            <ModalTitle>
              {panel === 'no-date' ? 'No or invalid expected date' : panel ? fullDayLabel(panel) : ''}
            </ModalTitle>
            <ModalDescription>
              {panelRows.length} order{panelRows.length === 1 ? '' : 's'}
              {panel === 'no-date'
                ? ' without a usable expected delivery date (missing or out of range).'
                : ' expected on this day.'}
            </ModalDescription>
          </ModalHeader>
          <div className="px-6 pb-6 flex max-h-[60vh] flex-col gap-2 overflow-y-auto" data-testid="calendar-day-panel">
            {panelRows.map((row) => (
              <React.Fragment key={row.id}>{renderRowCard(row)}</React.Fragment>
            ))}
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
}

/** The calendar's list params: the page's own queryParams with ONLY paging
 *  widened (the export/LPO precedent). Pure so a test can pin that the
 *  chips/family/search scope reaches the calendar untouched. */
export function calendarListParams<T extends { page?: number; pageSize?: number }>(queryParams: T): T {
  return { ...queryParams, page: 0, pageSize: 5000 };
}
